import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import api from '../api/axiosClient';

const IaManagement = ({ darkMode, toggleDarkMode, onSelect }) => {
  const generateRandomCode = () => Math.floor(100000 + Math.random() * 900000);

  const [iaList, setIaList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    ia_name: '',
    is_name: '',
    year: new Date().getFullYear(),
    code: generateRandomCode(),
    password: ''
  });
  const [formError, setFormError] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // Fetch all IA data
  const fetchIaData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/api/nisia/get-all');
      if (response.data.success) {
        setIaList(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to fetch IA data');
      }
    } catch (err) {
      console.error('Error fetching IA data:', err);
      setError(err.response?.data?.message || 'Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchIaData();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'year' || name === 'code' ? (value ? parseInt(value, 10) : '') : value
    }));
    setFormError(null);
  };

  // Handle form submission
  const handleAddIA = async (e) => {
    e.preventDefault();
    setFormError(null);

    // Validation
    if (!formData.ia_name.trim()) {
      setFormError('IA Name is required');
      return;
    }
    if (!formData.is_name.trim()) {
      setFormError('IS Name is required');
      return;
    }
    if (!formData.year || formData.year < 1900) {
      setFormError('Valid year is required');
      return;
    }
    if (!formData.code || formData.code < 1) {
      setFormError('Valid code is required');
      return;
    }
    if (!formData.password || formData.password.length < 3) {
      setFormError('Password must be at least 3 characters');
      return;
    }

    setFormLoading(true);
    try {
      const response = await api.post('/api/nisia/register', {
        ia_name: formData.ia_name.trim(),
        is_name: formData.is_name.trim(),
        year: formData.year,
        code: Number(formData.code),
        password: formData.password
      });

      if (response.data.success) {
        setSuccessMessage(`IA "${formData.ia_name}" registered successfully!`);
        setFormData({
          ia_name: '',
          is_name: '',
          year: new Date().getFullYear(),
          code: generateRandomCode(),
          password: ''
        });
        setShowAddForm(false);
        
        // Refresh the IA list
        setTimeout(() => {
          fetchIaData();
          setSuccessMessage(null);
        }, 2000);
      } else {
        setFormError(response.data.message || 'Failed to register IA');
      }
    } catch (err) {
      console.error('Error registering IA:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Error registering IA';
      setFormError(errorMsg);
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <Container darkMode={darkMode}>
      <Header darkMode={darkMode}>
        <Title>IA Management</Title>
        <Description>Manage Irrigation Association Credentials</Description>
      </Header>

      {successMessage && (
        <SuccessMessage darkMode={darkMode}>
          ✓ {successMessage}
        </SuccessMessage>
      )}

      {error && (
        <ErrorMessage darkMode={darkMode}>
          ✕ {error}
        </ErrorMessage>
      )}

      <ControlBar darkMode={darkMode}>
        <ButtonGroup>
          <AddButton
            darkMode={darkMode}
            onClick={() => {
              setShowAddForm(prev => {
                const next = !prev;
                if (next) {
                  setFormError(null);
                  setSuccessMessage(null);
                  setFormData({
                    ia_name: '',
                    is_name: '',
                    year: new Date().getFullYear(),
                    code: generateRandomCode(),
                    password: ''
                  });
                }
                return next;
              });
            }}
          >
            {showAddForm ? '✕ Cancel' : '+ Add New IA'}
          </AddButton>
          <RefreshButton darkMode={darkMode} onClick={fetchIaData} disabled={loading}>
            {loading ? 'Loading...' : '↻ Refresh'}
          </RefreshButton>
        </ButtonGroup>
        <CountBadge darkMode={darkMode}>
          Total: {iaList.length}
        </CountBadge>
      </ControlBar>

      {showAddForm && (
        <FormContainer darkMode={darkMode}>
          <FormTitle>Register New IA</FormTitle>
          
          {formError && (
            <FormErrorMessage darkMode={darkMode}>
              {formError}
            </FormErrorMessage>
          )}

          <Form onSubmit={handleAddIA}>
            <FormGroup>
              <Label darkMode={darkMode}>IA Name *</Label>
              <Input
                darkMode={darkMode}
                type="text"
                name="ia_name"
                value={formData.ia_name}
                onChange={handleInputChange}
                placeholder="Enter IA name"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label darkMode={darkMode}>IS Name *</Label>
              <Input
                darkMode={darkMode}
                type="text"
                name="is_name"
                value={formData.is_name}
                onChange={handleInputChange}
                placeholder="Enter IS name"
                required
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label darkMode={darkMode}>Year *</Label>
                <Input
                  darkMode={darkMode}
                  type="number"
                  name="year"
                  value={formData.year}
                  onChange={handleInputChange}
                  placeholder="Year"
                  min="1900"
                  max="2100"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label darkMode={darkMode}>Code *</Label>
                <Input
                  darkMode={darkMode}
                  type="number"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="Auto-generated 6-digit code"
                  min="1"
                  max="999999"
                  readOnly
                  required
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label darkMode={darkMode}>Password *</Label>
              <Input
                darkMode={darkMode}
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Min 3 characters"
                minLength="3"
                required
              />
            </FormGroup>

            <SubmitButton darkMode={darkMode} type="submit" disabled={formLoading}>
              {formLoading ? 'Registering...' : 'Register IA'}
            </SubmitButton>
          </Form>
        </FormContainer>
      )}

      <ContentWrapper darkMode={darkMode}>
        {loading ? (
          <LoadingMessage darkMode={darkMode}>Loading IA data...</LoadingMessage>
        ) : iaList.length === 0 ? (
          <EmptyMessage darkMode={darkMode}>
            No IAs registered yet. Click "Add New IA" to create one.
          </EmptyMessage>
        ) : (
          <TableWrapper darkMode={darkMode}>
            <Table darkMode={darkMode}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>IA Name</th>
                  <th>IS Name</th>
                  <th>Year</th>
                  <th>Code</th>
                  <th>Registered Date</th>
                </tr>
              </thead>
              <tbody>
                {iaList.map((ia, index) => (
                  <tr key={ia.id || index}>
                    <td>{ia.id}</td>
                    <td>
                      <CellContent darkMode={darkMode}>{ia.ia_name}</CellContent>
                    </td>
                    <td>
                      <CellContent darkMode={darkMode}>{ia.is_name}</CellContent>
                    </td>
                    <td>{ia.year}</td>
                    <td>
                      <CodeBadge darkMode={darkMode}>{ia.code}</CodeBadge>
                    </td>
                    <td>
                      <DateCell darkMode={darkMode}>
                        {ia.transdate ? new Date(ia.transdate).toLocaleDateString() : 'N/A'}
                      </DateCell>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </ContentWrapper>
    </Container>
  );
};

export default IaManagement;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${props => (props.darkMode ? '#121212' : '#f5f5f5')};
  padding: 20px;
  overflow: auto;
`;

const Header = styled.div`
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px 0;
  color: ${props => (props.darkMode ? '#ffffff' : '#1a1a1a')};
`;

const Description = styled.p`
  font-size: 14px;
  color: ${props => (props.darkMode ? '#999' : '#666')};
  margin: 0;
`;

const ControlBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px;
  background-color: ${props => (props.darkMode ? '#1e1e1e' : '#ffffff')};
  border-radius: 8px;
  border: 1px solid ${props => (props.darkMode ? '#333' : '#e0e0e0')};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const Button = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddButton = styled(Button)`
  background-color: #4caf50;
  color: white;

  &:hover:not(:disabled) {
    background-color: #45a049;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(76, 175, 80, 0.3);
  }
`;

const RefreshButton = styled(Button)`
  background-color: ${props => (props.darkMode ? '#333' : '#f0f0f0')};
  color: ${props => (props.darkMode ? '#fff' : '#333')};
  border: 1px solid ${props => (props.darkMode ? '#555' : '#ddd')};

  &:hover:not(:disabled) {
    background-color: ${props => (props.darkMode ? '#444' : '#e0e0e0')};
  }
`;

const CountBadge = styled.span`
  padding: 6px 12px;
  background-color: ${props => (props.darkMode ? '#333' : '#e8f5e9')};
  color: ${props => (props.darkMode ? '#4caf50' : '#2e7d32')};
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
`;

const SuccessMessage = styled.div`
  padding: 12px 16px;
  background-color: #e8f5e9;
  color: #2e7d32;
  border-left: 4px solid #4caf50;
  border-radius: 4px;
  margin-bottom: 15px;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  padding: 12px 16px;
  background-color: #ffebee;
  color: #c62828;
  border-left: 4px solid #f44336;
  border-radius: 4px;
  margin-bottom: 15px;
  font-size: 14px;
`;

const FormErrorMessage = styled(ErrorMessage)`
  margin-bottom: 15px;
`;

const FormContainer = styled.div`
  background-color: ${props => (props.darkMode ? '#1e1e1e' : '#ffffff')};
  border: 1px solid ${props => (props.darkMode ? '#333' : '#e0e0e0')};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`;

const FormTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 15px 0;
  color: ${props => (props.darkMode ? '#ffffff' : '#1a1a1a')};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${props => (props.darkMode ? '#ccc' : '#333')};
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${props => (props.darkMode ? '#444' : '#ddd')};
  border-radius: 6px;
  font-size: 14px;
  background-color: ${props => (props.darkMode ? '#2a2a2a' : '#ffffff')};
  color: ${props => (props.darkMode ? '#fff' : '#000')};
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #4caf50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
  }

  &::placeholder {
    color: ${props => (props.darkMode ? '#777' : '#aaa')};
  }
`;

const SubmitButton = styled(Button)`
  background-color: #2196f3;
  color: white;
  padding: 12px;
  margin-top: 10px;

  &:hover:not(:disabled) {
    background-color: #1976d2;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(33, 150, 243, 0.3);
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  background-color: ${props => (props.darkMode ? '#1e1e1e' : '#ffffff')};
  border: 1px solid ${props => (props.darkMode ? '#333' : '#e0e0e0')};
  border-radius: 8px;
  overflow: auto;
`;

const TableWrapper = styled.div`
  overflow: auto;
  height: 100%;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: ${props => (props.darkMode ? '#1e1e1e' : '#ffffff')};

  thead {
    background-color: ${props => (props.darkMode ? '#2a2a2a' : '#f5f5f5')};
    position: sticky;
    top: 0;
    z-index: 10;
  }

  th {
    padding: 15px;
    text-align: left;
    font-weight: 600;
    font-size: 13px;
    color: ${props => (props.darkMode ? '#ccc' : '#333')};
    border-bottom: 2px solid ${props => (props.darkMode ? '#333' : '#e0e0e0')};
  }

  td {
    padding: 12px 15px;
    border-bottom: 1px solid ${props => (props.darkMode ? '#333' : '#e0e0e0')};
    color: ${props => (props.darkMode ? '#ddd' : '#333')};
    font-size: 14px;
  }

  tbody tr:hover {
    background-color: ${props => (props.darkMode ? '#252525' : '#f9f9f9')};
  }
`;

const CellContent = styled.div`
  font-weight: 500;
  color: ${props => (props.darkMode ? '#fff' : '#000')};
`;

const CodeBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background-color: ${props => (props.darkMode ? '#333' : '#e3f2fd')};
  color: ${props => (props.darkMode ? '#2196f3' : '#1976d2')};
  border-radius: 4px;
  font-weight: 600;
  font-size: 13px;
`;

const DateCell = styled.div`
  font-size: 13px;
  color: ${props => (props.darkMode ? '#aaa' : '#666')};
`;

const LoadingMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-size: 16px;
  color: ${props => (props.darkMode ? '#999' : '#666')};
`;

const EmptyMessage = styled(LoadingMessage)`
  color: ${props => (props.darkMode ? '#888' : '#999')};
`;
